/**
 * Task status and priority badges: the state chip (glyph plus word, never
 * colour alone) and the approved-priority badge. A Backlog suggestion never
 * renders through the badge; the dashed proposal tag is its only dress.
 */

import type { ReactNode } from 'react';

import { useWorkspaceCopy } from './use-workspace-copy.js';

// --------------------------------------------------------------------------- //
// TaskStateChip
// --------------------------------------------------------------------------- //

/**
 * Distinct glyphs, one per state — geometry, not emoji, so they inherit the
 * chip's colour and flip nothing under RTL.
 */
const STATE_GLYPHS: Record<string, ReactNode> = {
  BACKLOG: (
    <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
      <circle
        cx="6"
        cy="6"
        r="4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="2.4 2"
      />
    </svg>
  ),
  READY: (
    <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
      <circle
        cx="6"
        cy="6"
        r="4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  ),
  IN_PROGRESS: (
    <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
      <circle
        cx="6"
        cy="6"
        r="4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path d="M6 1.5 A4.5 4.5 0 0 1 10.5 6 L6 6 Z" fill="currentColor" />
    </svg>
  ),
  IN_REVIEW: (
    <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
      <circle
        cx="6"
        cy="6"
        r="4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="6" cy="6" r="1.6" fill="currentColor" />
    </svg>
  ),
  DONE: (
    <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
      <path
        d="M2 6.5 L5 9.5 L10 3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  REJECTED: (
    <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
      <path
        d="M3 3 L9 9 M9 3 L3 9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  ),
};

const STATE_COLORS: Record<string, { bg: string; fg: string }> = {
  BACKLOG: { bg: 'var(--status-neutral-bg)', fg: 'var(--status-neutral-fg)' },
  READY: { bg: 'var(--status-info-bg)', fg: 'var(--status-info-fg)' },
  IN_PROGRESS: { bg: 'var(--status-live-bg)', fg: 'var(--status-live-fg)' },
  IN_REVIEW: { bg: 'var(--status-warning-bg)', fg: 'var(--status-warning-fg)' },
  DONE: { bg: 'var(--status-success-bg)', fg: 'var(--status-success-fg)' },
  REJECTED: { bg: 'var(--status-error-bg)', fg: 'var(--status-error-fg)' },
};

const FALLBACK_STATE_COLORS = {
  bg: 'var(--status-neutral-bg)',
  fg: 'var(--status-neutral-fg)',
} as const;

export function TaskStateChip({ status }: { readonly status: string }): ReactNode {
  const copy = useWorkspaceCopy();
  const colors = STATE_COLORS[status] ?? FALLBACK_STATE_COLORS;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 10px',
        borderRadius: 'var(--radius-tag)',
        background: colors.bg,
        color: colors.fg,
        font: 'var(--type-caption)',
        letterSpacing: 'var(--type-caption-tracking)',
        whiteSpace: 'nowrap',
      }}
    >
      {STATE_GLYPHS[status] ?? STATE_GLYPHS['BACKLOG']}
      {copy.statuses[status] ?? status}
    </span>
  );
}

// --------------------------------------------------------------------------- //
// PriorityBadge
// --------------------------------------------------------------------------- //

/**
 * One glyph per priority — direction drawn as geometry, so it inherits the
 * badge's ink and reads without colour: two chevrons up, one up, a level
 * bar, one down.
 */
const PRIORITY_GLYPHS: Record<string, ReactNode> = {
  P1: (
    <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
      <path
        d="M2.5 6.5 6 3l3.5 3.5M2.5 9.5 6 6l3.5 3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  P2: (
    <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
      <path
        d="M2.5 8 6 4.5 9.5 8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  P3: (
    <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
      <path
        d="M2.5 6h7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  ),
  P4: (
    <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
      <path
        d="M2.5 4.5 6 8l3.5-3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

/**
 * The *approved* priority, worn openly: glyph, weight, colour, and the
 * readable label together — remove any one and the badge still says what it
 * says. A Backlog suggestion never renders through this component; the
 * dashed proposal tag is its only dress, by the file-header rule.
 */
export function PriorityBadge({ priority }: { readonly priority: string }): ReactNode {
  const copy = useWorkspaceCopy();
  return (
    <span
      className="mj-priority-badge"
      data-priority={priority}
      title={copy.task.authoritativePriority}
    >
      {PRIORITY_GLYPHS[priority] ?? null}
      <span>{copy.priorities[priority] ?? priority}</span>
    </span>
  );
}
